/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Generic functions
 */
import React from 'react';
import { EventProvider } from '@aquila/core';
import { PublicClientApplication } from '@azure/msal-browser';
import { marked } from 'marked';
import { toJS } from 'mobx';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { msalConfig } from '../authConfig';
import metaConfig from './config/MetaConfig';
import * as constants from './constants';
import env from './env';

export default class utils {
  static convertSecondstoHoursandMin(readingTime) {
    const hours = Math.floor(readingTime / 3600);
    const min = Math.round((readingTime % 3600) / 60);

    return {
      hours,
      min
    };
  }

  static lazyWithPreload(component) {
    const lazyComponent = React.lazy(component);
    lazyComponent.preload = component;
    return lazyComponent;
  }

  static convertDate(date) {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 10, 30, 0);
  }

  static epochToUTC(epochTimestamp) {
    const date = new Date(epochTimestamp * 1000).toUTCString().replace('GMT', 'UTC');
    return date;
  }

  static setCookie() {
    let keys = window.sessionStorage.getItem(`${'msal.token.keys.'}${env.INT_AZURE_CLIENT_ID}`);
    keys = JSON.parse(keys).accessToken;
    if (keys !== null) {
      let value = window.sessionStorage.getItem(keys);
      value = JSON.parse(value);
      const { secret, expiresOn } = value;
      const expires = utils.epochToUTC(expiresOn);
      document.cookie = `myAzureProxySession= ${secret}; expires= ${expires}; path=/; domain=.pearson.com`;
    }
  }

  static setCDNTokenInCookie(str) {
    const epochTime = str?.value?.split('~');
    const expiresOn = epochTime[0].split('=')[1];
    const expires = utils.epochToUTC(expiresOn);
    document.cookie = `${str.name}=${str.value}; expires= ${expires}; path=/; domain=.pearson.com`;
  }

  static getCookieByName(cookieName) {
    const cookies = document.cookie;
    if (!cookies) {
      return false;
    }
    return cookies.split('; ').some((cookie) => cookie.split('=')[0] === cookieName);
  }

  static getToken() {
    let keys = window.sessionStorage.getItem(`${'msal.token.keys.'}${env.INT_AZURE_CLIENT_ID}`);
    keys = JSON.parse(keys).accessToken;
    if (keys !== null) {
      let value = window.sessionStorage.getItem(keys);
      value = JSON.parse(value);
      const { secret } = value;
      return secret;
    }
    return null;
  }

  static uuid() {
    return uuidv4();
  }

  static convertBoolean(str) {
    let bool = false;
    if (str && str !== null && str !== '') {
      const lowerStr = str.toLowerCase();
      if (lowerStr === 'y') {
        bool = true;
      }
    }
    return bool;
  }

  static authorFullName(data) {
    return data
      .map(
        (row) =>
          `${row.firstName ? row.firstName : ''} 
          ${row.otherName ? row.otherName : ''} ${row.lastName ? row.lastName : ''}`
      )
      .join(', ');
  }

  static isbnDisplayName(data) {
    return data.map((row) => `${row.displayISBN ? row.displayISBN : ''}`).join(', ');
  }

  static logEvent(event) {
    EventProvider.getEventManager().dispatch({
      event: event.event,
      category: event.category,
      bookId: window.sessionStorage.getItem('bookId'),
      userId: window.sessionStorage.getItem('userId'),
      data: {
        label: event.label,
        action: event.action,
        value: event.value
      }
    });
  }

  static async redirectToLogin() {
    // expire cookie
    const currentUrl = window.location.href;
    document.cookie = 'myAzureProxySession=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.pearson.com';
    const msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize().then(() => {
      msalInstance.logoutRedirect({
        postLogoutRedirectUri: currentUrl
      });
    });
  }

  static getEditionTypeValue = (editionTypeID) => {
    let editionTypeValue = '';
    if (editionTypeID && editionTypeID > 0) {
      const bookEdition = metaConfig.bookEditionTypeId.find((item) => item.id === editionTypeID);
      if (bookEdition) {
        editionTypeValue = ` (${bookEdition.value})`;
      }
    }
    return editionTypeValue;
  };

  static serverSideProcessStatus = (sspStatus) => {
    let status = null;
    if (sspStatus !== null) {
      switch (sspStatus) {
        case constants.STATUSES.FOXIT_PROCESS_FAILED:
        case constants.STATUSES.FOXIT_FILES_S3UPLOAD_FAILED:
        case constants.STATUSES.FAILED:
          status = 'common.FAILED';
          break;
        case constants.STATUSES.COMPLETED:
          status = 'common.SUCCESS';
          break;
        case constants.STATUSES.FOXIT_IN_PROGRESS:
        case constants.STATUSES.FOXIT_PROCESS_COMPLETED:
        case constants.STATUSES.FOXIT_FILES_S3UPLOAD_IN_PROGRESS:
          status = 'common.IN_PROGRESS';
          break;
        case constants.STATUSES.COVER_PAGE_NOT_FOUND:
          status = 'common.COVER_PAGE_NOT_FOUND';
          break;
        case constants.STATUSES.SLATES_NOT_FOUND:
          status = 'common.SLATES_NOT_FOUND';
          break;
        default:
          status = 'common.NOT_STARTED';
          break;
      }
    }
    return status;
  };

  static convertBytes = (bytes) => {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let l = 0;
    let n = parseInt(bytes, 10) || 0;

    // eslint-disable-next-line no-plusplus
    while (n >= 1024 && ++l) {
      n /= 1024;
    }

    return `${n.toFixed(n < 10 && l > 0 ? 1 : 0)} ${units[l]}`;
  };

  static disciplineCodeStructure = (dataArray) => {
    const disciplineCode = [];
    dataArray.forEach((discipline) => {
      disciplineCode.push({
        id: discipline.disciplineCodeId,
        value: `${discipline.discipline}${constants.PIPE}${
          discipline.subDiscipline !== null ? discipline.subDiscipline : ''
        }${constants.PIPE}${discipline.course}${constants.PIPE}${discipline.disciplineCode}`
      });
    });
    return disciplineCode;
  };

  static processStoreValue = (chatgptResponseUtilityStore) => {
    /* eslint-disable camelcase */
    let rowProcessed = {};
    const {
      summary,
      quiz,
      discuss,
      chat_enrichment,
      chat_intent,
      content_classifier,
      flashcards,
      topic,
      summary_enrichment,
      summaryTopic,
      quizValidateIntent,
      quizIntent,
      mcq,
      mcqMisconceptions,
      mcqSkillIdentifier,
      mcqQuestionGeneration,
      quizMcq,
      quizFreeResponse,
      freeResponse,
      mcqDistract,
      dictionarySummary,
      dictionaryQuiz,
      content_filter
    } = chatgptResponseUtilityStore;
    if (chatgptResponseUtilityStore) {
      const dictionarySummaryData = dictionarySummary && dictionarySummary.length > 0 ? dictionarySummary : [];
      const dictionaryQuizData = dictionaryQuiz && dictionaryQuiz.length > 0 ? dictionaryQuiz : [];
      const summaryData = summary && summary.length > 0 ? summary : [];
      const quizData = quiz && quiz.length > 0 && quiz ? quiz : [];
      const discussData = discuss && discuss.length > 0 && discuss ? discuss : [];
      const chatEnrichmentData = chat_enrichment && chat_enrichment.length > 0 ? chat_enrichment : [];
      const chatIntentData = chat_intent && chat_intent.length > 0 ? chat_intent : [];
      const flashcardsData = flashcards && flashcards.length > 0 ? flashcards : [];
      const topicData = topic && topic.length > 0 ? topic : [];
      const summaryEnrichmentData = summary_enrichment && summary_enrichment.length > 0 ? summary_enrichment : [];
      const summaryTopicData = summaryTopic && summaryTopic.length > 0 ? summaryTopic : [];
      const contentClassifier = content_classifier && content_classifier.length > 0 ? content_classifier : [];
      const quizValidateData = quizValidateIntent && quizValidateIntent.length > 0 ? quizValidateIntent : [];
      const quizIintentData = quizIntent && quizIntent.length > 0 ? quizIntent : [];
      const mcqData = mcq && mcq.length > 0 ? mcq : [];
      const mcqMisconceptionsData = mcqMisconceptions && mcqMisconceptions.length > 0 ? mcqMisconceptions : [];
      const mcqSkillIdentifierData = mcqSkillIdentifier && mcqSkillIdentifier.length > 0 ? mcqSkillIdentifier : [];
      const mcqQuestionGenerationData =
        mcqQuestionGeneration && mcqQuestionGeneration.length > 0 ? mcqQuestionGeneration : [];
      const quizMcqData = quizMcq && quizMcq.length > 0 ? quizMcq : [];
      const quizFreeResponseData = quizFreeResponse && quizFreeResponse.length > 0 ? quizFreeResponse : [];
      const freeResponseData = freeResponse && freeResponse.length > 0 ? freeResponse : [];
      const mcqDistractData = mcqDistract && mcqDistract.length > 0 ? mcqDistract : [];
      const contentFilterData = content_filter && content_filter.length > 0 ? content_filter : [];
      const combineData = [
        ...dictionarySummaryData,
        ...dictionaryQuizData,
        ...summaryData,
        ...discussData,
        ...quizData,
        ...chatEnrichmentData,
        ...chatIntentData,
        ...flashcardsData,
        ...topicData,
        ...contentClassifier,
        ...summaryEnrichmentData,
        ...summaryTopicData,
        ...quizValidateData,
        ...quizIintentData,
        ...mcqData,
        ...mcqMisconceptionsData,
        ...mcqSkillIdentifierData,
        ...mcqQuestionGenerationData,
        ...quizMcqData,
        ...quizFreeResponseData,
        ...freeResponseData,
        ...mcqDistractData,
        ...contentFilterData
      ];
      /* eslint-disable no-param-reassign */
      if (combineData[0] && combineData[0].type === 'chat_intent') {
        try {
          combineData[0].botResponse = JSON.parse(combineData[0].botResponse);
        } catch (err) {
          const { botResponse } = combineData[0];
          combineData[0] = { ...combineData[0], botResponse: botResponse[0] };
        }
      }
      const combineDatatoJS = JSON.parse(JSON.stringify(toJS(combineData)));
      // const { prompt } = combineDatatoJS[0];
      if (combineDatatoJS[0] && Object.keys(combineDatatoJS[0]).length > 0) {
        rowProcessed = Object.entries(combineDatatoJS[0]).reduce((obj, [key, value]) => {
          if (key !== null && value !== null) {
            obj[key] = value;
          }
          return obj;
        }, {});
      }
    }
    if (rowProcessed.type === 'chat_intent') {
      const { botResponse } = rowProcessed;
      let processBotResponse = '';
      try {
        processBotResponse = [JSON.parse(botResponse)];
      } catch (err) {
        const splitTwoText = botResponse.split('[BAD-GPT]');
        const normGPT = JSON.parse(splitTwoText[0].split('[NORM-GPT]')[1]);
        const badGPT = JSON.parse(splitTwoText[1]);
        processBotResponse = [normGPT, badGPT];
      }
      rowProcessed.botResponse = processBotResponse;
    }
    return rowProcessed;
  };

  static displayUserId = (data) => {
    if (data.userId === constants.SIGNED_OUT_USER_ID) {
      return constants.SIGNED_OUT_USER;
    }
    return data.userId;
  };

  static processChannelsDataResponse = (channelsUtility) => {
    let rowProcessed = {};
    const { content_filter, channelsResult } = channelsUtility;
    if (channelsUtility) {
      const channelsResultData = channelsResult && channelsResult.length > 0 ? channelsResult : [];
      const contentFilterData = content_filter && content_filter.length > 0 ? content_filter : [];
      const combineData = [...channelsResultData, ...contentFilterData];
      const combineDatatoJS = JSON.parse(JSON.stringify(toJS(combineData)));
      /* eslint-disable no-param-reassign */
      if (combineDatatoJS[0] && Object.keys(combineDatatoJS[0]).length > 0) {
        rowProcessed = Object.entries(combineDatatoJS[0]).reduce((obj, [key, value]) => {
          if (key !== null && value !== null) {
            obj[key] = value;
          }
          return obj;
        }, {});
      }
    }
    return rowProcessed;
  };

  static processChannelsSubTypeResponse = (channelsSubType) => {
    let returnVal = [];
    const { content_filter, channelsResult } = channelsSubType;
    if (channelsSubType) {
      const channelsResultData = channelsResult && channelsResult.length > 0 ? channelsResult : [];
      const contentFilterData = content_filter && content_filter.length > 0 ? content_filter : [];
      const combineData = [...channelsResultData, ...contentFilterData];
      returnVal = JSON.parse(JSON.stringify(toJS(combineData)));
    }
    return returnVal;
  };

  static weeksStartEndDate = (month, monthIndex, year) => {
    const getMomentDate = (start, end, index) => {
      const startDate = moment([year, monthIndex - 1, start]).format(constants.MM_DD_YYYY_FORMAT);
      const endDate = moment([year, monthIndex - 1, end]).format(constants.MM_DD_YYYY_FORMAT);
      const currentDate = moment().format(constants.MM_DD_YYYY_FORMAT);
      return {
        startDate,
        endDate,
        weekNumber: `Week ${index + 1}`,
        month: moment([year, monthIndex - 1, start]).format(constants.MMMM_FORMAT),
        disable: moment().year() === year && endDate >= currentDate
      };
    };
    const weekStartEndDay = [];
    const first = month.day() === 0 ? 6 : month.day() - 1;
    let day = 7 - first;
    const last = month.daysInMonth();
    const count = (last - day) / 7;
    weekStartEndDay.push(getMomentDate(1, day, 0));
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < count; i++) {
      weekStartEndDay.push(getMomentDate(day + 1, Math.min((day += 7), last), i + 1));
    }
    return weekStartEndDay;
  };

  static getSessionData = () => {
    let keys = window.sessionStorage.getItem('msal.account.keys');
    if (keys !== null && keys !== '[]') {
      keys = keys.replace(/[,"[\]]/g, '');
      let value = window.sessionStorage.getItem(keys);
      value = JSON.parse(value);
      const { username } = value;
      return username;
    }
    return null;
  };

  static containsMathML = (str) => {
    const mathmlRegex = /<math[\s\S]*?<\/math>/gi; // Regex to match <math> ... </math> tags
    return mathmlRegex.test(str);
  };

  static loadMathJax = () => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = constants.MATHJAX_CDN_URL;
    document.head.appendChild(script);
  };

  static loadMathJaxV3 = () => {
    if (window.MathJax && window.MathJax.version && window.MathJax.version.indexOf('3') !== 0) {
      window.MathJax = '';
    }
    if (!(window.MathJax && window.MathJax.startup) && typeof window.isMathjaxLoaded === 'undefined') {
      window.isMathjaxLoaded = true;
      // Create MathJax config
      const mathJaxConfigScript = document.createElement('script');
      mathJaxConfigScript.text = `
          window.MathJax = {
            loader: { load: ['[tex]/mhchem', '[tex]/mathtools', '[tex]/noerrors', 'input/tex', 'input/asciimath', 'output/chtml'] },
            startup: {
              ready() {
                const { HTMLDocument } = MathJax._.handlers.html.HTMLDocument;
                HTMLDocument.prototype.findPosition = function (N, index, delim, nodes) {
                  const adaptor = this.adaptor;
                  const inc = 1 / (nodes[N].length || 1);
                  let i = N;
                  for (const [node, n] of nodes[N]) {
                    if (index <= n && adaptor.kind(node) === '#text') {
                      return { i, node, n: Math.max(index, 0), delim };
                    }
                    index -= n;
                    i += inc;
                  }
                  return { node: null, n: 0, delim };
                };
                MathJax.startup.defaultReady();
              }
            },
            asciimath: { delimiters: [['\\\\$$', '\\\\$$']] },
            tex: {
              inlineMath: [['\\\\(', '\\\\)'], ['\\\\[', '\\\\]']],
              packages: { '[+]': ['mhchem', 'mathtools'] },
              macros: { prescript: ['{}^{#1}_{#2}', 2] }
            }
          };
        `;
      // Append config to head
      document.head.appendChild(mathJaxConfigScript);
      // Dynamically load MathJax script
      const mathJaxScript = document.createElement('script');
      mathJaxScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-mml-chtml.min.js';
      document.head.appendChild(mathJaxScript);
    }
  };

  static processMath = (element) => {
    if (typeof window.MathJax !== 'undefined' && typeof window.MathJax.Hub !== 'undefined') {
      if (element) {
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, element]);
      }
    } else {
      // Load MathJax dynamically
      utils.loadMathJax();
    }
  };

  static containsMarkDownText = (res) => {
    const str = typeof res === 'string' ? res : res.toString();
    // Regular expressions for common Markdown patterns
    const markdownPatterns = [
      /#\s/, // Headers (e.g., # Header)
      /\[.*?\]\(.*?\)/, // Links (e.g., [link](http://example.com))
      /\*\*.*?\*\*/, // Bold (e.g., **bold text**)
      /__.*?__/, // Bold (e.g., __bold text__)
      /\*.*?\*/, // Italic (e.g., *italic text*)
      /_.*?_/, // Italic (e.g., _italic text_)
      /^\s*[-*+]\s+/, // Unordered lists (e.g., - item)
      /^\s*\d+\.\s+/, // Ordered lists (e.g., 1. item)
      /```/, // Code blocks (e.g., ```code``` for inline code blocks)
      /~~/, // Strikethrough (e.g., ~~strikethrough~~)
      /!\[.*?\]\(.*?\)/ // Images (e.g., ![alt text](url))
    ];

    return markdownPatterns.some((pattern) => pattern.test(str));
  };

  static convertToMarkedDownString = (content) => {
    const str = typeof content === 'string' ? content : content.toString();
    const formattedText = str.replace(/\n\s*\n/g, '\n');
    const parsedData = marked
      .parse(formattedText.replaceAll('`', '@@').replaceAll('\\', '@!'))
      .replaceAll('<em>', '_')
      .replaceAll('</em>', '_');
    return parsedData.replaceAll('@@', '`').replaceAll('@!', '\\');
  };

  static textFromContentV3 = (content) =>
    new Promise((resolve, reject) => {
      if (
        (content.indexOf('`') >= 0 &&
          content.lastIndexOf('`') > 0 &&
          content.indexOf('`') !== content.lastIndexOf('`')) ||
        content.indexOf('\\') >= 0 ||
        content.includes('</math>')
      ) {
        // Create a temporary container element
        const tempContainer = document.createElement('span');
        // Set the content (string with math expressions) as innerHTML
        tempContainer.innerHTML = content;
        // Use MathJax.typesetPromise() to typeset only this container
        window.MathJax.typesetPromise([tempContainer])
          .then(() => {
            resolve(tempContainer.innerHTML); // Resolve the promise with the container's typeset HTML
            // Remove the temporary container after typesetting is done
            tempContainer.remove();
          })
          .catch((err) => {
            reject(err); // Reject the promise if an error occurs
          });
      } else {
        resolve(content);
      }
    });

  static convertToMarkedDownStringV3 = async (content) => {
    try {
      const checkCharExist = typeof content === 'string' && content && content.trim();
      if (checkCharExist && checkCharExist.length > 0) {
        return marked.parse(content);
      }
      return content;
    } catch (e) {
      return content;
    }
  };

  static getBookData = (data, isEpub = false) => {
    const bookData = {};
    if (isEpub) {
      bookData.bookId = data.bookId;
      bookData.courseId = data.bookId;
      bookData.indexId = data.indexId;
      bookData.basePath = '';
      bookData.author = data.author;
      bookData.thumbnailUrl = data.thumbnailUrl;
      bookData.title = data.title;
    } else {
      bookData.basePath = data.basePath;
      bookData.bookId = data.bookId;
      bookData.courseId = data.bookId;
      bookData.indexId = data.globalBookId;
      bookData.basePath = data.basePath;
      bookData.thumbnailUrl = data.thumbnailArt;
      bookData.title = data.title;
    }
    return bookData;
  };

  static getTenantId = (tenantName, tenantConfig) => {
    let tenTId = '';
    const { data } = toJS(tenantConfig);
    if (data.length > 0) {
      const match = data.find((config) => config.name === tenantName);
      if (match) {
        tenTId = match.tenantId;
      }
    }

    return tenTId;
  };

  static getTenantName = (tenantId, tenantConfig) => {
    let tenantName = '';
    const match = tenantConfig.find((config) => config.tenantId === tenantId);
    if (match) {
      tenantName = match.name;
    }

    return tenantName;
  };

  /* eslint-disable */
  static convertServerDateTime = (dateTime) => {
    let convertSecondsToDate = dateTime;
    if (typeof dateTime === 'string') {
      if (new Date(parseInt(dateTime)).getFullYear() === 1970) {
        convertSecondsToDate = new Date(parseInt(dateTime) * 1000).toLocaleString();
      } else {
        convertSecondsToDate = new Date(parseInt(dateTime)).toLocaleString();
      }
    } else {
      if (new Date(dateTime).getFullYear() === 1970) {
        convertSecondsToDate = new Date(dateTime * 1000).toLocaleString();
      } else {
        convertSecondsToDate = new Date(dateTime).toLocaleString();
      }
    }
    return convertSecondsToDate;
  };

  static sortBookPages = (pages) => {
    return pages.sort((a, b) => {
      if (a.pageNo === 'Cover') return -1;
      if (b.pageNo === 'Cover') return 1;
      if (a.pageNo === 'Blank page') return -1;
      if (b.pageNo === 'Blank page') return 1;
      if (a.pageNo === 'Blank') return -1;
      if (b.pageNo === 'Blank') return 1;

      const romanToNumber = (roman) => {
        const romanMap = {
          i: 1,
          ii: 2,
          iii: 3,
          iv: 4,
          v: 5,
          vi: 6,
          vii: 7,
          viii: 8,
          ix: 9,
          x: 10,
          xi: 11,
          xii: 12,
          xiii: 13,
          xiv: 14,
          xv: 15
        };
        return romanMap[roman] || 0;
      };

      const isRoman = (str) => /^(m{0,3})(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})$/.test(str);
      if (isRoman(a.pageNo) && isRoman(b.pageNo)) {
        return romanToNumber(a.pageNo) - romanToNumber(b.pageNo);
      } else if (isRoman(a.pageNo)) {
        return -1;
      } else if (isRoman(b.pageNo)) {
        return 1;
      }

      const numA = isNaN(a.pageNo) ? Infinity : Number(a.pageNo);
      const numB = isNaN(b.pageNo) ? Infinity : Number(b.pageNo);

      return numA - numB;
    });
  };

  static pageUrnToPageId = (pageUrn) => {
    if (pageUrn?.includes('.pdf')) return pageUrn.split('.')[0];
    return pageUrn;
  };

  static sortByServerDateTime = (records) => {
    return records.slice().sort((a, b) => {
      const dateA = new Date(a.serverDateTime);
      const dateB = new Date(b.serverDateTime);
      return dateA - dateB;
    });
  };

  static splitQuizData(data) {
    const result1 = [];
    const result2 = [];

    let foundPractice1 = false;
    let foundPractice2 = false;

    for (const item of data) {
      if (!foundPractice1 && item.type === constants.TYPE_QUIZ_PRACTICE_ONE) {
        result1.push(item);
        foundPractice1 = true;
        continue;
      }

      if (foundPractice1 && !foundPractice2 && item.type === constants.TYPE_QUIZ) {
        result1.push(item);
        continue;
      }

      if (item.type === constants.TYPE_QUIZ_PRACTICE_TWO) {
        result2.push(item);
        foundPractice2 = true;
        continue;
      }

      if (foundPractice2 && item.type === 'quiz') {
        result2.push(item);
      }
    }

    return [result1, result2];
  }

  static parseAttributes = (attributes) => {
    const attrs = [];
    const regex = /(\w+)="([^"]+)"/g;
    let match;
    // eslint-disable-next-line
    while ((match = regex.exec(attributes)) !== null) {
      attrs.push(` ${match[1]}="${match[2]}"`);
    }
    return attrs.join('');
  };

  static renderLinksVideos = (text) => {
    const linkRegex = /<a href="([^"]+)"([^>]*)>(.*?)<\/a>/g;
    const videoTagRegex = /<video([^>]+)>/g;
    const imgTagRegex = /<img[^>]*>/g;
    const iframeRegex = /<iframe[^>]*>/g;
    let finalRespStr = text;
    if (linkRegex.test(finalRespStr)) {
      finalRespStr = finalRespStr?.replace(linkRegex, (match, url, attributes, linkText) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer"${this.parseAttributes(
          attributes
        )}>${linkText}</a>`;
      });
    }

    if (imgTagRegex.test(finalRespStr)) {
      finalRespStr = finalRespStr?.replace(imgTagRegex, (imgTag) => {
        if (!imgTag.includes('width=')) {
          return imgTag?.replace('<img', '<img style="width: 100%;"');
        }
        return imgTag;
      });
    }

    if (iframeRegex.test(finalRespStr)) {
      finalRespStr = finalRespStr?.replace(iframeRegex, (iframeTag) => {
        if (!iframeTag.includes('width=')) {
          return iframeTag?.replace(
            '<iframe',
            '<iframe allowfullscreen style="width: 100%; height: 500px; border: 1px solid;"'
          );
        }
        return iframeTag;
      });
    }

    if (videoTagRegex.test(finalRespStr)) {
      return finalRespStr?.replace(videoTagRegex, (match) => {
        const hasStyle = /style="[^"]+"/.test(match);
        const hasControls = /controls/.test(match);
        let enhancedTag = match;
        if (!hasStyle) {
          enhancedTag = enhancedTag?.replace('<video', '<video style="width: 100%;"');
        }
        if (!hasControls) {
          enhancedTag = enhancedTag?.replace('<video', '<video controls');
        }
        return enhancedTag;
      });
    }
    return finalRespStr;
  };

  static getEncodedPayload = (payload) => {
    const updatedPayload = {
      ...payload,
      promptStart: utils.encodedData(payload.promptStart),
      promptEnd: utils.encodedData(payload.promptEnd)
    };
    return updatedPayload;
  };

  static buildRequestPartsFromPayload = (event) => {
    const payload = event.payload?.tenantId ? _.omit(event.payload, constants.TENANT_ID) : event.payload;
    let headers = event.payload?.tenantId ? { [constants.BERLIN_TENANT_ID_HEADER]: event.payload?.tenantId } : {};
    headers = Object.keys(headers).length > 0 ? headers : undefined;
    return { payload, headers };
  };

  static buildHeadersWithTenantId = (event) => {
    let headers = event.tenantId ? { [constants.BERLIN_TENANT_ID_HEADER]: event.tenantId } : {};
    headers = Object.keys(headers).length > 0 ? headers : undefined;
    return headers;
  };

  static encodedData(data) {
    let updatedData = data.map((item) => {
      const updatedData = new TextEncoder().encode(item.content);
      return {
        ...item,
        content: btoa(String.fromCharCode(...updatedData)) //encodeURIComponent(item.content)
      };
    });
    return updatedData;
  }

  static getEncodedPayload = (payload) => {
    const updatedPayload = {
      ...payload,
      promptStart: utils.encodedData(payload.promptStart),
      promptEnd: utils.encodedData(payload.promptEnd)
    };
    return updatedPayload;
  };

  static generateReadableTrace(logJson) {
    const seenSteps = new Set();
    const result = [];

    const stepNameMap = {
      LangGraph: 'Graph Execution Started',
      __start__: 'Start Node Started',
      __end__: 'Workflow Completed',
      'ChannelWrite<...>': 'Send input to next step',
      'ChannelWrite<branch:to:agent>': 'Routing to Agent',
      'ChannelWrite<branch:to:supervisor>': 'Routing to Supervisor',
      RunnableSequence: 'Run Task Sequence',
      'Branch<agent,tools,__end__>': 'Decision: Agent, Tool, or End',
      supervisor: 'Handled by Supervisor',
      agent: 'Handled by Agent',
      tools: 'Executing Tool',
      prompt: 'Generate Routing Prompt'
    };

    const simplifyName = (name) => {
      if (stepNameMap[name]) return stepNameMap[name];
      const matched = name.match(/ChannelWrite<([^>]*)>/);
      if (matched) {
        if (matched[1].includes('agent')) return 'Routing to Agent';
        if (matched[1].includes('supervisor')) return 'Routing to Supervisor';
        return 'Send input to next step';
      }
      return name;
    };

    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    const extractToolInput = (data) => {
      const argsString = data?.tool_calls?.[0]?.function?.arguments;
      if (!argsString || argsString === '{}') return 'No additional parameters';

      try {
        const args = JSON.parse(argsString);
        if (!args || Object.keys(args).length === 0) return 'No additional parameters';
        return Object.entries(args)
          .map(([k, v]) => `${capitalize(k)}: ${v}`)
          .join(' | ');
      } catch {
        return argsString;
      }
    };

    // const summarizeContent = (data, type, name) => {
    //   if (!data) return null;

    //   const isTool = type.includes('TOOL');

    //   if (isTool && (data.tool_calls || data.function_call)) {
    //     return extractToolInput(data);
    //   }

    //   const msg = data?.messages?.[0];
    //   let content = msg?.content || JSON.stringify(msg || data);
    //   try {
    //     const parsed = JSON.parse(content);
    //     if (typeof parsed === 'string') return parsed;

    //     const parts = [];
    //     if (parsed.input) parts.push(`"${parsed.input}"`);
    //     if (parsed.bookId) parts.push(`Book: ${parsed.bookId}`);
    //     if (parsed.pageIds?.length) parts.push(`Pages: ${parsed.pageIds.join(', ')}`);
    //     if (parsed.threadId) parts.push(`Thread: ${parsed.threadId}`);
    //     if (parsed.userId) parts.push(`User: ${parsed.userId}`);
    //     return parts.join(' | ') || null;
    //   } catch {
    //     return content.length > 300 ? content.slice(0, 300) + '...' : content;
    //   }
    // };

    const summarizeContent = (data, type, name) => {
      if (!data) return null;

      const isTool = type.includes('TOOL');

      // Handle tool inputs
      if (isTool && (data.tool_calls || data.function_call)) {
        return extractToolInput(data);
      }

      // Look inside .messages[0].content
      const msg = data?.messages?.[0];
      let content = msg?.content || JSON.stringify(msg || data);

      try {
        const parsed = JSON.parse(content);
        if (typeof parsed === 'string') return parsed;

        const parts = [];

        if (parsed.input) parts.push(`"${parsed.input}"`);
        if (parsed.bookId) parts.push(`Book: ${parsed.bookId}`);
        if (parsed.pageIds?.length) parts.push(`Pages: ${parsed.pageIds.join(', ')}`);
        if (parsed.threadId) parts.push(`Thread: ${parsed.threadId}`);
        if (parsed.userId) parts.push(`User: ${parsed.userId}`);
        if (parsed.productModel) parts.push(`Model: ${parsed.productModel}`);
        if (parsed.requestId) parts.push(`Request ID: ${parsed.requestId}`);

        return parts.join(' | ') || null;
      } catch {
        return content.length > 300 ? content.slice(0, 300) + '...' : content;
      }
    };

    for (const event of logJson.executionLog) {
      const { type, name, data } = event;
      const stepKey = `${type}_${name}`;
      if (seenSteps.has(stepKey)) continue;
      seenSteps.add(stepKey);

      const readableType = type.replace('on_', '').replace('_', ' ').toUpperCase();
      const displayName = simplifyName(name);
      const isToolStep = readableType.includes('TOOL');

      const inputSummary = summarizeContent(data?.input, readableType, name);
      let outputSummary = summarizeContent(data?.output, readableType, name);

      // TOOL END: If output equals input or is null → show success message
      if (isToolStep && type === 'on_tool_end' && (!outputSummary || outputSummary === inputSummary)) {
        outputSummary = `Success (input was routed to ${name})`;
      }

      let stepText = `Step: ${displayName} [${readableType}]`;
      const lines = [];

      if (inputSummary) lines.push(`- Input: ${inputSummary}`);
      if (outputSummary) lines.push(`- Output: ${outputSummary}`);

      if (lines.length) stepText += `\n${lines.join('\n')}`;
      result.push(stepText);
    }

    return result.join('\n\n');
  }

  static generateReadableDataFlow(logData) {
    const logs = logData.executionLog;
    const nodes = {};
    const links = [];

    const extractText = (messages) => {
      if (!messages?.length) return '';
      const raw = messages[0].content || messages[0]?.lc_kwargs?.content || '';
      try {
        const parsed = JSON.parse(raw);
        return parsed?.input || raw;
      } catch {
        return raw;
      }
    };

    const friendlyLabel = (name) => {
      if (name === '__start__') return 'Start Node';
      if (name.startsWith('ChannelWrite<branch:to:supervisor')) return 'Send to Supervisor';
      if (name.startsWith('ChannelWrite<branch:to:agent')) return 'Send to Agent';
      if (name.startsWith('ChannelWrite')) return 'Routing Message';
      if (name.startsWith('RunnableSequence')) return 'Internal Agent Chain';
      if (name.startsWith('prompt')) return 'Routing Prompt';
      if (name.startsWith('Branch<')) return 'Branch Decision';
      if (name === 'tools') return 'Tool Group Execution';
      if (name === 'agent') return 'Agent Router';
      if (name === 'supervisor') return 'Supervisor Node';
      if (name === 'LangGraph') return 'Workflow Start';
      // if (name.includes('Explain_Agent')) return 'Explain Agent';
      // if (name.includes('GetRAGTool')) return 'Retrieve Book Content Tool';
      // if (name.includes('GetImageDescription')) return 'Image Description Tool';
      // if (name.includes('GetPriorSessionChatTool')) return 'Previous Chat Context Tool';
      // if (name.includes('transfer_to_explain_agent')) return 'Transfer to Explain Agent Logic';
      return name; // fallback
    };

    // Collect node info
    logs.forEach((log) => {
      const { name, type, data } = log;
      if (!nodes[name]) nodes[name] = {};
      if (type === 'on_chain_start') nodes[name].input = extractText(data.input?.messages);
      if (type === 'on_chain_end') nodes[name].output = extractText(data.output?.messages || data.output);
    });

    // Order nodes based on appearance in logs
    const ordered = Object.entries(nodes).map(([name, val], idx) => ({
      id: idx + 1,
      rawName: name,
      label: friendlyLabel(name),
      input: val.input || '',
      output: val.output || ''
    }));

    // Link nodes based on output → input flow match
    for (let i = 0; i < ordered.length; i++) {
      const currentOutput = ordered[i].output?.trim();
      for (let j = i + 1; j < ordered.length; j++) {
        const nextInput = ordered[j].input?.trim();
        if (currentOutput && nextInput && currentOutput === nextInput) {
          links.push({ from: ordered[i].label, to: ordered[j].label });
          break;
        }
      }
    }

    // Print readable trace
    const res = ordered
      .map((node, index) => {
        const link = links.find((l) => l.from === node.label);
        const arrow = link ? ` → ${link.to}` : '';
        return `🔹 ${node.label}${arrow}\n   👉 Input: ${node.input}\n   🏁 Output: ${node.output}\n`;
      })
      .join('\n');

    return res;
  }

  static fetchAggregatedCountData(chatgptUtilityTrendsData) {
    const aggregatedObj = chatgptUtilityTrendsData.reduce((acc, item) => {
      if (!acc[item.bookId]) {
        acc[item.bookId] = {
          title: item.title,
          bookId: item.bookId,
          likeCount: 0,
          disLikeCount: 0,
          explainCount: 0,
          summaryCount: 0,
          quizMcqCount: 0,
          unknownCount: 0,
          blockWordsCount: 0,
          unAnsweredCount: 0,
          unAnsweredUnknownIntentCount: 0,
          unAnsweredNoCSGDocCount: 0,
          unAnsweredNoTopicFoundCount: 0,
          unAnsweredProblemSolveCount: 0
        };
      }

      acc[item.bookId].likeCount += item.likeCount;
      acc[item.bookId].disLikeCount += item.disLikeCount;
      acc[item.bookId].explainCount += item.explainCount;
      acc[item.bookId].summaryCount += item.summaryCount;
      acc[item.bookId].quizMcqCount += item.quizMcqCount;
      acc[item.bookId].unknownCount += item.unknownCount;
      acc[item.bookId].blockWordsCount += item.blockWordsCount;
      acc[item.bookId].unAnsweredCount += item.unAnsweredCount;
      acc[item.bookId].unAnsweredUnknownIntentCount += item.unAnsweredUnknownIntentCount;
      acc[item.bookId].unAnsweredNoCSGDocCount += item.unAnsweredNoCSGDocCount;
      acc[item.bookId].unAnsweredNoTopicFoundCount += item.unAnsweredNoTopicFoundCount;
      acc[item.bookId].unAnsweredProblemSolveCount += item.unAnsweredProblemSolveCount;

      return acc;
    }, {});
    return Object.values(aggregatedObj);
  }

  static fetchChannelsIdWiseData(channelsUtilityTrendsData) {
    const aggregatedObj = channelsUtilityTrendsData.reduce((acc, item) => {
      if (!acc[item.bookId]) {
        acc[item.bookId] = {
          bookId: item.bookId,
          videoSummaryCount: 0,
          questionIntentCount: 0,
          likeCount: 0,
          disLikeCount: 0
        };
      }
      acc[item.bookId].videoSummaryCount += item.videoSummaryCount;
      acc[item.bookId].questionIntentCount += item.questionIntentCount;
      acc[item.bookId].likeCount += item.likeCount;
      acc[item.bookId].disLikeCount += item.disLikeCount;
      return acc;
    }, {});
    return Object.values(aggregatedObj);
  }

  static getStartDate24HoursAgo() {
    const currentDate = new Date();
    const startDate = new Date(currentDate.getTime() - 24 * 60 * 60 * 1000);
    const month = (startDate.getMonth() + 1).toString().padStart(2, '0');
    const day = startDate.getDate().toString().padStart(2, '0');
    const year = startDate.getFullYear();
    return `${month}/${day}/${year}`;
  }

  /**
   * Escape a value for CSV (quotes, commas, newlines).
   * @param {*} value - Cell value
   * @returns {string}
   */
  static escapeCSVValue(value) {
    let str =
      value === null || value === undefined || (Array.isArray(value) && value.length === 0) ? '' : String(value);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    if (str.includes(',') || str.includes('\n') || str.includes('\r')) str = `"${str}"`;
    return str.trim();
  }

  /**
   * Build CSV string from data and headers. Headers: [{ name, key }] for header row and row[key] for each cell.
   * @param {Array<Object>} data - Array of row objects
   * @param {Array<{ name: string, key: string }>} headers - Column definitions
   * @returns {string} CSV content
   */
  static buildCSVWithHeaders(data, headers) {
    if (!Array.isArray(headers) || headers.length === 0) return '';
    const headerRow = headers.map((h) => utils.escapeCSVValue(h.name)).join(',');
    if (!Array.isArray(data) || data.length === 0) return headerRow;
    const rows = data.map((row) =>
      headers.map((h) => utils.escapeCSVValue(row[h.key] !== undefined ? row[h.key] : '')).join(',')
    );
    return [headerRow, ...rows].join('\n');
  }

  /**
   * Trigger browser download of a CSV string.
   * @param {string} csvContent - Full CSV string
   * @param {string} filename - Download filename (e.g. 'report.csv')
   */
  static downloadCSV(csvContent, filename) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
